import type { CaptureResult } from "posthog-js";
import { sanitizePostHogProperties } from "./posthog-privacy.ts";

const WEB_VITALS_SAMPLE_RATE = 0.1;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isOpaqueScriptError(event: CaptureResult): boolean {
  const exceptions: unknown = event.properties.$exception_list;
  if (!Array.isArray(exceptions)) {
    const values = event.properties.$exception_values;
    const sources = event.properties.$exception_sources;
    return Array.isArray(values) && values.length === 1 && values[0] === "Script error." &&
      (sources == null || (Array.isArray(sources) && sources.length === 0));
  }
  if (exceptions.length !== 1) return false;
  const exception: unknown = exceptions[0];
  if (!isRecord(exception) || exception.type !== "Error" || exception.value !== "Script error.") return false;
  const sources = event.properties.$exception_sources;
  if (sources != null && (!Array.isArray(sources) || sources.length > 0)) return false;
  if (exception.stacktrace == null) return true;
  return isRecord(exception.stacktrace) && Array.isArray(exception.stacktrace.frames) &&
    exception.stacktrace.frames.length === 0;
}

function isNativeMediaControlsError(event: CaptureResult): boolean {
  const exceptions: unknown = event.properties.$exception_list;
  if (!Array.isArray(exceptions) || exceptions.length !== 1) return false;
  const exception: unknown = exceptions[0];
  if (!isRecord(exception) || exception.type !== "TypeError" || exception.value !== "Type error") return false;
  const stacktrace = exception.stacktrace;
  if (!isRecord(stacktrace) || !Array.isArray(stacktrace.frames) || stacktrace.frames.length !== 1) return false;
  const frame: unknown = stacktrace.frames[0];
  if (!isRecord(frame) || frame.function !== "sortedTrackListForMenu") return false;
  return frame.filename == null || frame.filename === "" ||
    frame.filename === "[native code]" || frame.filename === "Unknown Source";
}

const EMPTY_RANGES_VALUE = "Can't find variable: EmptyRanges";
const JAVA_BRIDGE_VALUE = "Error invoking postMessage: Java object is gone";
const IN_APP_BROWSER_FRAME_PREFIX = "iabjs://";
const FIRST_PARTY_ASSET_PATH = "/_next/";
// Exact messages from objects that browsers, wallets, extensions and link
// scanners inject into the page. None of these names exist in our code.
const INJECTED_SIGNATURES: ReadonlyArray<{ type: string; value: RegExp }> = [
  { type: "TypeError", value: /^undefined is not an object \(evaluating 'window\.__firefox__\.[\w$.]+'\)$/ },
  { type: "ReferenceError", value: /^Can't find variable: __firefox__$/ },
  { type: "TypeError", value: /^undefined is not an object \(evaluating 'window\.ethereum\.selectedAddress = undefined'\)$/ },
  { type: "TypeError", value: /^undefined is not an object \(evaluating 'window\.webkit\.messageHandlers'\)$/ },
  { type: "Error", value: /^'Error' captured as exception with message: 'Invalid call to runtime\.sendMessage\(\)\. Tab not found\.'$/ },
  { type: "UnhandledRejection", value: /^Non-Error promise rejection captured with value: Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+$/ },
];

interface SingleException {
  type: string;
  value: string;
  frames: unknown[];
}

/** Exactly one exception with raw SDK frames; null for chains or malformed metadata. */
function getSingleException(event: CaptureResult): SingleException | null {
  const exceptions: unknown = event.properties.$exception_list;
  if (!Array.isArray(exceptions) || exceptions.length !== 1) return null;
  const exception: unknown = exceptions[0];
  if (!isRecord(exception) || typeof exception.type !== "string" || typeof exception.value !== "string") return null;
  const stacktrace = exception.stacktrace;
  if (stacktrace == null) return { type: exception.type, value: exception.value, frames: [] };
  if (!isRecord(stacktrace) || !Array.isArray(stacktrace.frames)) return null;
  return { type: exception.type, value: exception.value, frames: stacktrace.frames };
}

function getFrameFilename(frame: unknown): string | null | undefined {
  if (!isRecord(frame)) return null;
  if (frame.filename == null) return undefined;
  return typeof frame.filename === "string" ? frame.filename : null;
}

/** Third-party noise that cannot come from our bundles; first-party frames keep it. */
function isThirdPartyNoise(event: CaptureResult): boolean {
  const exception = getSingleException(event);
  if (!exception) return false;
  const { type, value, frames } = exception;
  // WebKit-internal reference with no script location.
  if (type === "ReferenceError" && value === EMPTY_RANGES_VALUE) return frames.length === 0;
  // Meta's Android in-app browser bridge after its Java object is released.
  if (type === "Error" && value === JAVA_BRIDGE_VALUE) {
    return frames.length > 0 && frames.every((frame) =>
      getFrameFilename(frame)?.startsWith(IN_APP_BROWSER_FRAME_PREFIX) === true);
  }
  if (!INJECTED_SIGNATURES.some((signature) => signature.type === type && signature.value.test(value))) return false;
  return frames.every((frame) => {
    const filename = getFrameFilename(frame);
    return filename !== null && !filename?.includes(FIRST_PARTY_ASSET_PATH);
  });
}

function isFilteredException(event: CaptureResult): boolean {
  try {
    return isOpaqueScriptError(event) || isNativeMediaControlsError(event) || isThirdPartyNoise(event);
  } catch {
    // Unreadable exception metadata is kept rather than thrown into capture().
    return false;
  }
}

function shouldKeepWebVitals(sessionId: unknown): boolean {
  if (typeof sessionId !== "string" || !sessionId) return true;
  let hash = 2166136261;
  for (let index = 0; index < sessionId.length; index++) {
    hash = Math.imul(hash ^ sessionId.charCodeAt(index), 16777619);
  }
  return (hash >>> 0) % 100 < WEB_VITALS_SAMPLE_RATE * 100;
}

export function beforeSendToPostHog(event: CaptureResult | null): CaptureResult | null {
  try {
    return prepareForPostHog(event);
  } catch {
    // posthog-js lets a before_send error escape capture() into the caller.
    // Drop this one event instead of throwing or sending it unsanitized.
    return null;
  }
}

function prepareForPostHog(event: CaptureResult | null): CaptureResult | null {
  if (!event) return event;
  if (event.event === "$exception" && isFilteredException(event)) return null;
  if (event.event === "$web_vitals" && !shouldKeepWebVitals(event.properties.$session_id)) return null;
  const properties = sanitizePostHogProperties(event.properties, true);
  const environment = process.env.NEXT_PUBLIC_VERCEL_ENV;
  const hasMetadata = Boolean(environment);
  const result: CaptureResult = {
    ...event,
    properties: {
      ...properties,
      ...(hasMetadata ? {
        platform: "web",
        deployment_environment: environment,
        git_commit: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
        telemetry_schema_version: 2,
      } : {}),
      ...(event.event === "$web_vitals" ? {
        web_vitals_sample_rate: typeof event.properties.$session_id === "string" && event.properties.$session_id
          ? WEB_VITALS_SAMPLE_RATE : 1,
      } : {}),
    },
  };
  for (const key of ["$set", "$set_once"] as const) {
    const value = event[key];
    if (isRecord(value)) result[key] = sanitizePostHogProperties(value);
  }
  const hasTopLevelChanges = result.$set !== event.$set || result.$set_once !== event.$set_once;
  return properties === event.properties && !hasMetadata && event.event !== "$web_vitals" &&
    !hasTopLevelChanges ? event : result;
}
